import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../../contexts/AuthContext';
import { paymentConfigService } from '../../services/paymentConfigService';
import {
  AppCredentialFormValues,
  AppCredentialProviderChoice,
  GENERIC_APP_CREDENTIAL_LABEL,
  GENERIC_PROVIDER_VALUE,
  MULTI_PROVIDER_CANDIDATES_ENABLED,
  toAppCredentialSavePayload,
  findExistingCredentialForSameApp,
  getMultiProviderGateMessage,
  getUniqueAppCredentialConfigs,
} from '../../utils/paymentConfig';
import { hasMenuPermission } from '../../utils/permissions';
import {
  PAYMENT_FORM_AUTO_COMPLETE,
  PAYMENT_INPUT_AUTO_COMPLETE,
  PAYMENT_PASSWORD_AUTO_COMPLETE,
  PaymentFormAutofillGuard,
} from './paymentFormAutocomplete';
import { showPaymentSaveWarnings } from './paymentSaveWarnings';
import {
  AppCredentialConfig,
  AppCredentialConfigSaveRequest,
  CLIENT_PLATFORM_MAP,
  ClientPlatform,
  CredentialType,
  PAYMENT_PROVIDER_MAP,
  PaymentProvider,
} from '../../types';

const { Paragraph, Title, Text } = Typography;

type ProviderChoice = AppCredentialProviderChoice;

const GENERIC_CREDENTIAL_TYPES: CredentialType[] = ['WECHAT_MINIAPP', 'WECHAT_MP'];
const GENERIC_CLIENT_PLATFORMS: ClientPlatform[] = ['WEAPP', 'WECHAT_H5'];

const PAYMENT_PROVIDER_OPTIONS: PaymentProvider[] = [
  'WECHAT_OFFICIAL',
  'YEEPAY',
  'ALIPAY_OFFICIAL',
  'YUNHUITENG',
];
const CLIENT_PLATFORM_OPTIONS: ClientPlatform[] = ['WEAPP', 'WECHAT_H5', 'H5', 'ALIPAY'];
const CREDENTIAL_TYPE_OPTIONS: { label: string; value: CredentialType }[] = [
  { label: '微信小程序', value: 'WECHAT_MINIAPP' },
  { label: '微信公众号/服务号', value: 'WECHAT_MP' },
  { label: '支付宝应用', value: 'ALIPAY_APP' },
];

const getProviderLabel = (provider?: PaymentProvider) =>
  provider ? PAYMENT_PROVIDER_MAP[provider] || provider : GENERIC_APP_CREDENTIAL_LABEL;
const getClientPlatformLabel = (platform?: ClientPlatform) => (platform ? CLIENT_PLATFORM_MAP[platform] || platform : '-');
const getCredentialTypeLabel = (type?: CredentialType) =>
  CREDENTIAL_TYPE_OPTIONS.find(item => item.value === type)?.label || type || '-';

type AppCredentialSecretField = 'appSecret' | 'appPrivateKey' | 'alipayPublicKey' | 'certificateContent';

export const getAppCredentialSecretFields = (
  credentialType?: CredentialType,
  paymentProvider?: PaymentProvider,
): AppCredentialSecretField[] => {
  if (paymentProvider === 'YUNHUITENG') {
    return [];
  }
  if (credentialType === 'ALIPAY_APP') {
    return ['appPrivateKey', 'alipayPublicKey', 'certificateContent'];
  }
  return ['appSecret'];
};

export const getYunhuitengCredentialDefaults = (): Pick<
  AppCredentialConfigSaveRequest,
  'clientPlatform' | 'credentialType'
> => ({
  clientPlatform: 'WEAPP',
  credentialType: 'WECHAT_MINIAPP',
});

const removeEmptySecrets = (values: AppCredentialFormValues): AppCredentialFormValues => {
  const next = { ...values };
  (['appSecret', 'appPrivateKey', 'alipayPublicKey', 'certificateContent'] as const).forEach(key => {
    if (next[key] === '') {
      delete next[key];
    }
  });
  if (next.gatewayAppId === '') {
    delete next.gatewayAppId;
  }
  return next;
};

const PaymentAppCredentialConfig: React.FC = () => {
  const { menus, permissions } = useAuth();
  const canSaveConfig = hasMenuPermission(menus, permissions, 'PAYMENT_CONFIG', 'WRITE');
  const canUpdateSecrets = hasMenuPermission(menus, permissions, 'PAYMENT_SECRET', 'WRITE');
  const canMaintain = canSaveConfig || canUpdateSecrets;
  const [configs, setConfigs] = useState<AppCredentialConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<AppCredentialConfig | null>(null);
  const [form] = Form.useForm<AppCredentialFormValues>();
  const credentialType = Form.useWatch('credentialType', form);
  const paymentProvider = Form.useWatch('paymentProvider', form);
  const isGeneric = paymentProvider === GENERIC_PROVIDER_VALUE;
  const isYunhuiteng = paymentProvider === 'YUNHUITENG';
  const scopedProvider = isGeneric ? undefined : (paymentProvider as PaymentProvider | undefined);

  const providerOptions = useMemo(
    () => [
      { label: `${GENERIC_APP_CREDENTIAL_LABEL}（推荐）`, value: GENERIC_PROVIDER_VALUE as ProviderChoice },
      ...PAYMENT_PROVIDER_OPTIONS.map(value => ({ label: `仅限${getProviderLabel(value)}`, value: value as ProviderChoice })),
    ],
    [],
  );
  const platformOptions = useMemo(() => {
    const platforms = isYunhuiteng
      ? (['WEAPP'] as ClientPlatform[])
      : isGeneric
        ? GENERIC_CLIENT_PLATFORMS
        : CLIENT_PLATFORM_OPTIONS;
    return platforms.map(value => ({ label: getClientPlatformLabel(value), value }));
  }, [isGeneric, isYunhuiteng]);
  const credentialTypeOptions = useMemo(() => {
    if (isYunhuiteng) {
      return CREDENTIAL_TYPE_OPTIONS.filter(item => item.value === 'WECHAT_MINIAPP');
    }
    if (isGeneric) {
      return CREDENTIAL_TYPE_OPTIONS.filter(item => GENERIC_CREDENTIAL_TYPES.includes(item.value));
    }
    return CREDENTIAL_TYPE_OPTIONS;
  }, [isGeneric, isYunhuiteng]);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentConfigService.queryPaymentConfig();
      if (res.status !== 1) {
        message.error(res.msg || '获取应用资质配置失败');
        return;
      }
      setConfigs(getUniqueAppCredentialConfigs(res.data?.platforms || []));
    } catch {
      message.error('获取应用资质配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      paymentProvider: GENERIC_PROVIDER_VALUE,
      clientPlatform: 'WEAPP',
      credentialType: 'WECHAT_MINIAPP',
    });
    setModalOpen(true);
  };

  const openEditModal = (record: AppCredentialConfig) => {
    setEditingRecord(record);
    form.setFieldsValue({
      appCredentialId: record.appCredentialId,
      paymentProvider: record.paymentProvider ?? GENERIC_PROVIDER_VALUE,
      clientPlatform: record.clientPlatform,
      appId: record.appId,
      appName: record.appName,
      credentialType: record.credentialType,
      gatewayAppId: record.gatewayAppId,
    });
    setModalOpen(true);
  };

  const handleProviderChange = (provider: ProviderChoice) => {
    if (provider === 'YUNHUITENG') {
      form.setFieldsValue(getYunhuitengCredentialDefaults());
      return;
    }
    if (provider === GENERIC_PROVIDER_VALUE) {
      const currentType = form.getFieldValue('credentialType') as CredentialType | undefined;
      const currentPlatform = form.getFieldValue('clientPlatform') as ClientPlatform | undefined;
      if (!currentType || !GENERIC_CREDENTIAL_TYPES.includes(currentType)) {
        form.setFieldsValue({ credentialType: 'WECHAT_MINIAPP' });
      }
      if (!currentPlatform || !GENERIC_CLIENT_PLATFORMS.includes(currentPlatform)) {
        form.setFieldsValue({ clientPlatform: 'WEAPP' });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const clientPlatform = values.clientPlatform;
      const appId = values.appId?.trim();
      if (!clientPlatform || !appId) {
        message.warning('请填写客户端平台与微信小程序 AppID');
        return;
      }
      if (values.paymentProvider === 'YUNHUITENG') {
        if (clientPlatform !== 'WEAPP' || values.credentialType !== 'WECHAT_MINIAPP') {
          message.error('云汇腾首期仅支持微信小程序应用资质');
          return;
        }
        if (!values.gatewayAppId?.trim()) {
          message.warning('请填写云汇腾应用 ID');
          return;
        }
      }
      if (!MULTI_PROVIDER_CANDIDATES_ENABLED) {
        const existing = findExistingCredentialForSameApp(configs, {
          clientPlatform,
          appId,
          appCredentialId: editingRecord?.appCredentialId,
        });
        if (existing) {
          message.error(getMultiProviderGateMessage());
          return;
        }
      }
      setSaving(true);
      const payload = toAppCredentialSavePayload(
        removeEmptySecrets({
          ...values,
          appCredentialId: editingRecord?.appCredentialId,
          clientPlatform,
          appId,
          gatewayAppId: values.gatewayAppId?.trim(),
        }),
      );
      const res = await paymentConfigService.saveAppCredentialConfigs([payload]);
      if (res.status === 1) {
        message.success('应用资质配置已保存');
        showPaymentSaveWarnings(res.data);
        setModalOpen(false);
        await loadConfigs();
      } else {
        message.error(res.msg || '应用资质配置保存失败');
      }
    } catch {
      // 表单校验错误不提示，避免重复打扰。
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<AppCredentialConfig> = [
    {
      title: '应用名称',
      dataIndex: 'appName',
      key: 'appName',
      width: 160,
    },
    {
      title: '微信小程序 AppID',
      dataIndex: 'appId',
      key: 'appId',
      width: 200,
      render: (value: string) => <Text copyable code>{value || '-'}</Text>,
    },
    {
      title: '云汇腾应用 ID',
      dataIndex: 'gatewayAppId',
      key: 'gatewayAppId',
      width: 180,
      render: (value: string | undefined, record) =>
        record.paymentProvider === 'YUNHUITENG' ? (
          <Text copyable code>{value || '-'}</Text>
        ) : (
          '-'
        ),
    },
    {
      title: '平台',
      dataIndex: 'clientPlatform',
      key: 'clientPlatform',
      width: 120,
      render: (value: ClientPlatform) => <Tag>{getClientPlatformLabel(value)}</Tag>,
    },
    {
      title: '资质类型',
      dataIndex: 'credentialType',
      key: 'credentialType',
      width: 140,
      render: (value: CredentialType) => getCredentialTypeLabel(value),
    },
    {
      title: '支付服务商',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      width: 150,
      render: (value?: PaymentProvider) => (
        <Tag color={value ? 'blue' : 'green'}>{getProviderLabel(value)}</Tag>
      ),
    },
    {
      title: '配置完整性',
      dataIndex: 'configComplete',
      key: 'configComplete',
      width: 110,
      render: (value: boolean) => <Tag color={value ? 'green' : 'red'}>{value ? '完整' : '缺失'}</Tag>,
    },
    {
      title: '敏感字段状态',
      key: 'sensitiveStatus',
      render: (_: unknown, record: AppCredentialConfig) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">{record.sensitiveStatus?.summary || '-'}</Text>
          {record.sensitiveStatus?.updatedAt && (
            <Text type="secondary">更新时间：{record.sensitiveStatus.updatedAt}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_: unknown, record: AppCredentialConfig) => (
        <Tooltip title={!canMaintain ? '缺少支付配置维护权限' : ''}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            disabled={!canMaintain}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
        </Tooltip>
      ),
    },
  ];

  const secretFields = getAppCredentialSecretFields(credentialType, scopedProvider);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={3}>应用资质配置</Title>
            <Paragraph type="secondary">
              维护微信小程序、微信公众号、支付宝应用等应用资质。微信小程序和公众号推荐使用“通用”资质：同一个 AppID
              只配一次，在支付路由中与任意服务商的商户配置组合；云汇腾应用 ID 在商户配置中按子商户填写。
            </Paragraph>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={loadConfigs}>
              刷新
            </Button>
            <Tooltip
              title={
                !canMaintain
                  ? '缺少支付配置维护权限'
                  : !MULTI_PROVIDER_CANDIDATES_ENABLED
                    ? '可新增首条资质；同一 AppID 的第二服务商候选暂未开放'
                    : ''
              }
            >
              <Button type="primary" icon={<PlusOutlined />} disabled={!canMaintain} onClick={openAddModal}>
                新增应用资质
              </Button>
            </Tooltip>
          </Space>
        </Space>
        {!canMaintain && (
          <Alert type="warning" showIcon message="当前账号没有支付配置维护权限，可查看但不可保存。" />
        )}
        {!MULTI_PROVIDER_CANDIDATES_ENABLED && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="多服务商路由功能尚未开放"
            description={getMultiProviderGateMessage()}
          />
        )}
      </Card>

      <Card>
        <Table
          rowKey={record => String(record.appCredentialId)}
          columns={columns}
          dataSource={configs}
          loading={loading}
          scroll={{ x: 1280 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑应用资质配置' : '新增应用资质配置'}
        open={modalOpen}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} autoComplete={PAYMENT_FORM_AUTO_COMPLETE}>
          <PaymentFormAutofillGuard />
          <Form.Item
            name="paymentProvider"
            label="适用服务商"
            rules={[{ required: true, message: '请选择适用服务商' }]}
            extra={
              editingRecord
                ? '已被路由引用的资质不能切换适用服务商，请新建资质后在支付路由中切换。'
                : undefined
            }
          >
            <Select options={providerOptions} onChange={value => handleProviderChange(value as ProviderChoice)} />
          </Form.Item>
          {isGeneric && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="通用应用资质说明"
              description={
                <>
                  通用资质只描述“这个应用是谁”，可在支付路由中与微信官方、云汇腾等任意服务商的商户配置组合，同一 AppID 只需配置一次。
                  {credentialType === 'WECHAT_MINIAPP' &&
                    ' 小程序登录在旧“微信应用”中找不到该 AppID 时，会优先使用这里的 AppSecret；修改 AppSecret 后需重启主服务才会生效。'}
                </>
              }
            />
          )}
          {isYunhuiteng && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="云汇腾专属应用资质说明"
              description="兼容存量配置。新接入推荐选择“通用”，并在云汇腾商户配置中按子商户填写云汇腾应用 ID；商户配置中填写的值优先于这里。"
            />
          )}
          <Form.Item name="clientPlatform" label="客户端平台" rules={[{ required: true, message: '请选择客户端平台' }]}>
            <Select options={platformOptions} disabled={isYunhuiteng} />
          </Form.Item>
          <Form.Item name="credentialType" label="资质类型" rules={[{ required: true, message: '请选择资质类型' }]}>
            <Select options={credentialTypeOptions} disabled={isYunhuiteng} />
          </Form.Item>
          <Form.Item name="appName" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}>
            <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="例如：默认微信小程序" />
          </Form.Item>
          <Form.Item
            name="appId"
            label={isYunhuiteng ? '微信小程序 AppID' : '真实 AppID'}
            rules={[{ required: true, message: isYunhuiteng ? '请输入微信小程序 AppID' : '请输入真实 AppID' }]}
            extra={
              !MULTI_PROVIDER_CANDIDATES_ENABLED
                ? '约束阶段完成前，同一平台和真实 AppID 不可再创建其他服务商候选资质。'
                : undefined
            }
          >
            <Input
              autoComplete={PAYMENT_INPUT_AUTO_COMPLETE}
              placeholder={isYunhuiteng ? '请输入微信小程序 AppID' : '请输入客户端真实 AppID'}
            />
          </Form.Item>
          {isYunhuiteng && (
            <Form.Item
              name="gatewayAppId"
              label="云汇腾应用 ID"
              rules={[{ required: true, message: '请输入云汇腾应用 ID' }]}
              extra="对应服务端 configParams.gatewayAppId，与上方微信小程序 AppID 不同。"
            >
              <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="请输入云汇腾 gatewayAppId" />
            </Form.Item>
          )}
          {secretFields.includes('appSecret') && (
            <Form.Item name="appSecret" label="AppSecret">
              <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="微信应用密钥，留空表示不更新" />
            </Form.Item>
          )}
          {secretFields.includes('appPrivateKey') && (
            <Form.Item name="appPrivateKey" label="支付宝应用私钥">
              <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="留空表示不更新" />
            </Form.Item>
          )}
          {secretFields.includes('alipayPublicKey') && (
            <Form.Item name="alipayPublicKey" label="支付宝公钥">
              <Input.Password autoComplete={PAYMENT_PASSWORD_AUTO_COMPLETE} placeholder="留空表示不更新" />
            </Form.Item>
          )}
          {secretFields.includes('certificateContent') && (
            <Form.Item name="certificateContent" label="支付宝证书内容">
              <Input.TextArea rows={4} autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="留空表示不更新" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  );
};

export default PaymentAppCredentialConfig;
